import {
  APPLY_CHUNK_SIZE,
  officeSlugBackfillService,
  type ApplyRowResult,
} from "../../../api/officeSlugBackfill.service.js";

/**
 * Everything a chunked run produced: the per-office results that came back,
 * and the failure that ended it early — or null for a run that sent every
 * chunk. A partial run's results are the most valuable thing it has (the
 * rollback CSV is built from them and stored nowhere else), so they are
 * returned alongside the failure rather than being thrown away with it.
 */
export type ChunkedApplyOutcome = {
  results: ApplyRowResult[];
  failure: string | null;
};

/**
 * Runs the office ids through the apply endpoint in chunks of
 * APPLY_CHUNK_SIZE, sequentially, banking every chunk that comes back.
 *
 * Deliberately resolves rather than throws: a chunk that fails stops the run,
 * and the caller still gets everything the earlier chunks returned.
 */
export async function runChunkedApply(
  officeIds: string[],
  onProgress: (done: number, total: number) => void,
): Promise<ChunkedApplyOutcome> {
  const results: ApplyRowResult[] = [];
  onProgress(0, officeIds.length);

  for (let start = 0; start < officeIds.length; start += APPLY_CHUNK_SIZE) {
    const chunk = officeIds.slice(start, start + APPLY_CHUNK_SIZE);
    try {
      const response = await officeSlugBackfillService.apply(chunk);
      results.push(...response.results);
    } catch (applyError) {
      const message = applyError instanceof Error ? applyError.message : String(applyError);
      // A request that failed may still have written rows: a gateway timeout
      // cuts the connection, not the transaction the API is part-way through.
      return {
        results,
        failure:
          `Stopped after ${results.length.toLocaleString()} of ` +
          `${officeIds.length.toLocaleString()} offices: ${message} ` +
          "Offices in the failed batch may or may not have been written — " +
          "re-scan before retrying.",
      };
    }
    onProgress(Math.min(start + chunk.length, officeIds.length), officeIds.length);
  }

  return { results, failure: null };
}
